// CalendarioControles.tsx — barra de controles del calendario admin (round
// 2026-09-15).
//
// A la vista quedan siempre el rango de fechas, «Refrescar» y el botón
// «Filtros», que muestra u oculta un panel con lo demás. Sin una elección
// guardada, el panel parte cerrado en el celular y abierto en escritorio (ver
// panelFiltros.ts). En el celular también van en el panel «Mes completo»,
// «Reiniciar», «Cambio masivo» y Mercado. El botón muestra cuántos filtros hay
// activos, así se nota que la grilla está filtrada aunque el panel esté cerrado.
import { Button, DatePicker, Input, Select, Switch, Tooltip } from "antd";
import {
  DollarOutlined,
  DownOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { getMsg } from "../../utils/antdStatic";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { PALETAS } from "../../utils/mercadoPaletas";
import {
  excedeRangoCalendario,
  MENSAJE_RANGO_CALENDARIO_MAXIMO,
  type RangoCalendarioISO,
} from "../../utils/rangoCalendario";
import type { PropFilters, PropSortKey } from "./useCalendarioLogic";
import { contarFiltrosActivos, filtrosVacios, usePanelFiltros } from "./panelFiltros";

const { RangePicker } = DatePicker;

const DISPLAY_FMT = "DD-MM-YYYY";
const ISO_FMT = "YYYY-MM-DD";

type RangoUi = [Dayjs | null, Dayjs | null];

const DIMENSIONES = [
  { key: "region", ph: "Región" },
  { key: "comuna", ph: "Comuna" },
  { key: "sector", ph: "Sector" },
  { key: "cliente", ph: "Cliente" },
] as const;

export type CalendarioControlesProps = {
  isMobile: boolean;
  esAdmin: boolean;
  rangeUi: RangoUi;
  setRangeUi: (rango: RangoUi) => void;
  range: RangoCalendarioISO;
  setRange: (rango: RangoCalendarioISO) => void;
  monthShortcutValue: Dayjs | null;
  onReiniciar: () => void;
  refrescando: boolean;
  onRefrescar: () => void;
  onCambioMasivo: () => void;
  mercadoOn: boolean;
  setMercadoOn: (on: boolean) => void;
  mercadoPaletaId: string;
  setMercadoPaletaId: (id: string) => void;
  codeSearch: string;
  setCodeSearch: (valor: string) => void;
  codeSearching: boolean;
  onCodeSearch: (valor: string) => void;
  propSearch: string;
  setPropSearch: (valor: string) => void;
  guestSearch: string;
  setGuestSearch: (valor: string) => void;
  propSort: PropSortKey;
  setPropSort: (orden: PropSortKey) => void;
  propFilters: PropFilters;
  setPropFilters: (filtros: PropFilters) => void;
  propFilterOptions: Record<keyof PropFilters, string[]>;
};

export default function CalendarioControles(p: CalendarioControlesProps) {
  const { isMobile, esAdmin } = p;
  const panel = usePanelFiltros(isMobile);
  // Hay un solo panel por página, así que basta un id fijo. El de useId() de
  // React 19.0 lleva «:» y rompe los selectores CSS que arma jsdom.
  const idPanel = "cal-panel-filtros";
  const activos = contarFiltrosActivos(p.propSearch, p.guestSearch, p.propFilters);
  // En el celular los controles van a lo ancho y con el alto normal, más fácil
  // de tocar; en escritorio, compactos como antes.
  const tamano = isMobile ? "middle" : "small";

  const irA = (desde: Dayjs, hasta: Dayjs) => {
    p.setRangeUi([desde, hasta]);
    p.setRange({ from: desde.format(ISO_FMT), to: hasta.format(ISO_FMT) });
  };

  // ── Rango ────────────────────────────────────────────────────────────────
  // Tope de 14 meses (utils/rangoCalendario.ts, round 2026-09-11). Un rango
  // rechazado avisa y no se aplica.
  const desde = (
    <DatePicker
      value={p.rangeUi[0]}
      format={DISPLAY_FMT}
      allowClear={false}
      inputReadOnly
      placeholder="Desde"
      onChange={(val) => {
        if (!val) return;
        const hasta = p.rangeUi[1];
        if (hasta && val.isAfter(hasta, "day")) return;
        if (hasta && excedeRangoCalendario(val, hasta)) {
          getMsg()?.error({ content: MENSAJE_RANGO_CALENDARIO_MAXIMO, duration: 5 });
          return;
        }
        if (hasta) irA(val, hasta);
        else p.setRangeUi([val, hasta]);
      }}
    />
  );

  const hasta = (
    <DatePicker
      value={p.rangeUi[1]}
      format={DISPLAY_FMT}
      allowClear={false}
      inputReadOnly
      placeholder="Hasta"
      onChange={(val) => {
        if (!val) return;
        const desdeD = p.rangeUi[0];
        if (desdeD && desdeD.isAfter(val, "day")) return;
        if (desdeD && excedeRangoCalendario(desdeD, val)) {
          getMsg()?.error({ content: MENSAJE_RANGO_CALENDARIO_MAXIMO, duration: 5 });
          return;
        }
        if (desdeD) irA(desdeD, val);
        else p.setRangeUi([desdeD, val]);
      }}
    />
  );

  const rangoEscritorio = (
    <RangePicker
      value={p.rangeUi}
      format={DISPLAY_FMT}
      allowClear={false}
      onChange={(vals) => {
        const [desdeD, hastaD] = (vals ?? []) as RangoUi;
        // Rechazado, el selector VUELVE al rango vigente: antes se quedaba
        // mostrando el rango rechazado mientras la grilla seguía en el anterior.
        if (desdeD && hastaD && excedeRangoCalendario(desdeD, hastaD)) {
          getMsg()?.error({ content: MENSAJE_RANGO_CALENDARIO_MAXIMO, duration: 5 });
          p.setRangeUi([dayjs(p.range.from), dayjs(p.range.to)]);
          return;
        }
        p.setRangeUi([desdeD ?? null, hastaD ?? null]);
        if (!desdeD || !hastaD || desdeD.isAfter(hastaD, "day")) return;
        p.setRange({ from: desdeD.format(ISO_FMT), to: hastaD.format(ISO_FMT) });
      }}
    />
  );

  // Atajo «Mes completo»: elegir mes y año salta el rango al mes entero. El
  // valor se deriva del rango (monthShortcutValue). Round 2026-07-11.
  const mesCompleto = (
    <DatePicker
      picker="month"
      placeholder="Mes completo"
      format="MMMM YYYY"
      allowClear={false}
      inputReadOnly={isMobile}
      value={p.monthShortcutValue}
      style={isMobile ? undefined : { width: 170 }}
      title="Salta el rango a un mes completo (día 1 al último)"
      onChange={(m) => {
        if (!m) return;
        irA(m.startOf("month"), m.endOf("month"));
      }}
    />
  );

  const reiniciar = <Button onClick={p.onReiniciar}>Reiniciar</Button>;

  // Refrescar SUAVE: vuelve a traer eventos y precios de las propiedades
  // abiertas, sin recargar, sin colapsar lo expandido y sin mover el scroll.
  const refrescar = (
    <Button
      icon={<ReloadOutlined />}
      loading={p.refrescando}
      onClick={p.onRefrescar}
      title="Vuelve a traer eventos y precios sin recargar ni cerrar lo abierto"
    >
      Refrescar
    </Button>
  );

  // ── Acciones de admin ────────────────────────────────────────────────────
  const cambioMasivo = esAdmin && (
    <Button
      icon={<DollarOutlined />}
      onClick={p.onCambioMasivo}
      title="Aplica precio, condición, min noches o restricción a varias propiedades a la vez"
    >
      Cambio masivo
    </Button>
  );

  // Fila informativa de Mercado: switch para toda la grilla y paleta propia del
  // calendario, guardados en el navegador. Round 2026-07-24.
  const mercado = esAdmin && (
    <span
      className="cal-mercado-toggle"
      title="Muestra bajo cada propiedad una fila con la ocupación estimada y el precio sugerido del mercado (informativa, sin interacción)"
    >
      <LineChartOutlined style={{ opacity: 0.7 }} />
      <span className="cal-mercado-toggle-lbl">Mercado</span>
      <Switch size="small" checked={p.mercadoOn} onChange={p.setMercadoOn} />
      {p.mercadoOn && (
        <Select
          size="small"
          value={p.mercadoPaletaId}
          onChange={p.setMercadoPaletaId}
          style={{ width: 150 }}
          title="Paleta de colores de la fila de mercado (propia del calendario)"
          options={PALETAS.map((paleta) => ({
            value: paleta.id,
            label: (
              <span className="cal-paleta-opt">
                <span className="cal-paleta-swatches">
                  {paleta.colores.map((c, i) => (
                    <span key={i} className="cal-paleta-sw" style={{ background: c }} />
                  ))}
                </span>
                {paleta.nombre}
              </span>
            ),
          }))}
        />
      )}
    </span>
  );

  // ── Búsquedas, orden y filtros (presentación: no vuelven a pedir datos) ──
  const buscarCodigo = (
    <Input.Search
      size={tamano}
      allowClear
      loading={p.codeSearching}
      placeholder="Código de estadía o plataforma…"
      value={p.codeSearch}
      onChange={(e) => p.setCodeSearch(e.target.value)}
      onSearch={p.onCodeSearch}
      enterButton
      suffix={
        <Tooltip
          title={
            <span>
              Busca la estadía por cualquier código y salta a ella en el calendario:
              <br />• <b>Código de estadía AF</b> interno (#1472)
              <br />• <b>Código de plataforma</b> Airbnb/Booking (ej. MJ23J)
              <br />• <b>ID de Stays</b> y <b>número corto</b> manual (AF-…)
              <br />• Nombre del <b>huésped</b>
              <br />
              Busca en lo cargado y, si no está, en todo el histórico.
            </span>
          }
        >
          <InfoCircleOutlined style={{ color: "#bfbfbf" }} />
        </Tooltip>
      }
      style={{ width: isMobile ? "100%" : 240 }}
    />
  );

  const buscarPropiedad = (
    <Input.Search
      size={tamano}
      allowClear
      placeholder="Propiedad, cliente o propietario…"
      value={p.propSearch}
      onChange={(e) => p.setPropSearch(e.target.value)}
      style={{ width: isMobile ? "100%" : 220 }}
    />
  );

  const buscarHuesped = (
    <Input.Search
      size={tamano}
      allowClear
      placeholder="Arrendatario (huésped)…"
      value={p.guestSearch}
      onChange={(e) => p.setGuestSearch(e.target.value)}
      style={{ width: isMobile ? "100%" : 190 }}
    />
  );

  const orden = (
    <Select<PropSortKey>
      size={tamano}
      value={p.propSort}
      onChange={p.setPropSort}
      style={{ width: isMobile ? "100%" : 150 }}
      options={[
        { value: "nombre", label: "Orden: Nombre" },
        { value: "region", label: "Orden: Región" },
        { value: "comuna", label: "Orden: Comuna" },
        { value: "sector", label: "Orden: Sector" },
        { value: "cliente", label: "Orden: Cliente" },
      ]}
    />
  );

  // Cada filtro aparece solo si hay 2 valores distintos o más: con uno solo,
  // filtrar no aporta.
  const filtros = DIMENSIONES.filter(({ key }) => p.propFilterOptions[key].length >= 2).map(
    ({ key, ph }) => (
      <Select
        key={key}
        mode="multiple"
        allowClear
        size={tamano}
        placeholder={ph}
        value={p.propFilters[key]}
        onChange={(vals) => p.setPropFilters({ ...p.propFilters, [key]: vals as string[] })}
        options={p.propFilterOptions[key].map((n) => ({ value: n, label: n }))}
        // fuzzyIncludes pliega tildes y ñ ("valparaiso" encuentra "Valparaíso").
        filterOption={(input, option) => fuzzyIncludes(option?.label, input)}
        maxTagCount="responsive"
        style={isMobile ? { width: "100%" } : { minWidth: 130 }}
      />
    ),
  );

  const limpiar = activos > 0 && (
    <Button
      type="link"
      size="small"
      className="cal-ctrl-limpiar"
      onClick={() => {
        p.setPropSearch("");
        p.setGuestSearch("");
        p.setPropFilters(filtrosVacios());
      }}
    >
      Limpiar filtros ({activos})
    </Button>
  );

  // Simbología: misma paleta que la vista cliente. El color depende de QUIÉN
  // CREÓ el registro, no de quién lo ve (docs/calendario-logica.md §6).
  const leyenda = (
    <div className="cal-legend cal-legend-admin">
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-kind-booking" />
        Arriendo
      </span>
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-cleaning" />
        Aseo
      </span>
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-kind-block is-from-client" />
        Bloqueo cliente
      </span>
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-kind-block" />
        Bloqueo admin
      </span>
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-kind-maintenance is-from-client" />
        Mantención cliente
      </span>
      <span className="cal-legend-item">
        <span className="cal-legend-swatch is-kind-maintenance" />
        Mantención admin
      </span>
    </div>
  );

  const botonFiltros = (
    <Button
      className={`cal-ctrl-filtros${panel.abierto ? " is-abierto" : ""}`}
      icon={<FilterOutlined />}
      onClick={panel.alternar}
      aria-expanded={panel.abierto}
      aria-controls={idPanel}
      aria-label={
        activos > 0
          ? `Filtros, ${activos} ${activos === 1 ? "activo" : "activos"}`
          : "Filtros"
      }
    >
      Filtros
      {activos > 0 && <span className="cal-ctrl-contador">{activos}</span>}
      <DownOutlined className="cal-ctrl-flecha" />
    </Button>
  );

  if (isMobile) {
    return (
      <div className="cal-controles is-movil">
        <div className="cal-ctrl-dos">
          {desde}
          {hasta}
        </div>
        <div className="cal-ctrl-dos">
          {botonFiltros}
          {refrescar}
        </div>
        <div id={idPanel} className="cal-panel-filtros" hidden={!panel.abierto}>
          <div className="cal-ctrl-dos">
            {mesCompleto}
            {reiniciar}
          </div>
          {esAdmin && (
            <div className="cal-ctrl-acciones">
              {cambioMasivo}
              {mercado}
            </div>
          )}
          {buscarCodigo}
          {buscarPropiedad}
          {buscarHuesped}
          <div className="cal-ctrl-dos">
            {orden}
            {filtros}
          </div>
          {limpiar}
          {leyenda}
          <div className="cal-ctrl-tip">Tip: toca dos veces una celda vacía para crear un evento</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cal-controles">
      <div className="cal-ctrl-principal">
        <span>Rango:</span>
        {rangoEscritorio}
        {mesCompleto}
        {reiniciar}
        {refrescar}
        {cambioMasivo}
        {mercado}
        {botonFiltros}
      </div>
      <div id={idPanel} className="cal-panel-filtros" hidden={!panel.abierto}>
        <div className="cal-sortfilter">
          {buscarCodigo}
          {buscarPropiedad}
          {buscarHuesped}
          {orden}
          {filtros}
          {limpiar}
        </div>
        {leyenda}
      </div>
    </div>
  );
}
